import { type RefObject, useEffect } from 'react'

/**
 * Blurs focused text fields when the user mousedown's on non-interactive UI
 * inside `shellRef`. Helps with Syncfusion/EJ2 toolbar inputs that stay
 * focused while clipped or visually de-emphasized, which otherwise leaves a
 * blinking caret on arbitrary clicks.
 *
 * Scoped to the reports module by passing the `.reports-page` root ref.
 */
export function useBlurFocusedInputOnNonFieldClick(
  shellRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const onMouseDownCapture = (e: MouseEvent): void => {
      const shell = shellRef.current
      if (!shell) return

      const t = e.target
      if (!(t instanceof Element)) return
      if (!shell.contains(t)) return

      if (
        t.closest(
          'input, textarea, select, button, a, [role="button"], label, [contenteditable="true"]',
        )
      ) {
        return
      }

      const ae = document.activeElement
      if (ae instanceof HTMLInputElement || ae instanceof HTMLTextAreaElement) {
        if (ae.contains(t) || t === ae) {
          return
        }
        if (t.closest('input, textarea')) {
          return
        }
        ae.blur()
        return
      }

      if (ae instanceof HTMLElement && ae.isContentEditable) {
        if (ae.contains(t) || t === ae) {
          return
        }
        if (t.closest('[contenteditable="true"]')) {
          return
        }
        ae.blur()
      }
    }

    document.addEventListener('mousedown', onMouseDownCapture, true)
    return () =>
      document.removeEventListener('mousedown', onMouseDownCapture, true)
  }, [shellRef])
}
